import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WizardShell, { WizardStatus } from '../../components/WizardShell';
import Callout from '../../components/Callout';
import { getVertical, type SizeTranche } from '../../data/verticals';
import { useProject } from '../../lib/useProject';
import { cx } from '../../lib/cx';

const fmt = (n: number) => n.toLocaleString('en-US');

function TrancheOption({
  tranche,
  selected,
  onSelect,
}: {
  tranche: SizeTranche;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cx(
        'w-full rounded-lg border p-5 text-left transition-colors',
        selected
          ? 'border-green-600 bg-green-050 shadow-[inset_0_0_0_2px_var(--color-green-600)]'
          : 'border-grey-200 bg-white shadow-sm hover:border-green-600',
      )}
    >
      <span className="t-h3 block text-ink">{tranche.label}</span>
      <span className="t-meta mt-1 block text-grey-600">{tranche.anchor}</span>
    </button>
  );
}

export default function Size() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { project, loading, loadError, saveState, setField, flush } = useProject(id);
  const [busy, setBusy] = useState(false);
  const [overriding, setOverriding] = useState(false);

  const vertical = project?.verticalId ? getVertical(project.verticalId) : undefined;

  /** Only computed when the vertical knows how, and only from answers we have. */
  const derived = useMemo(() => {
    if (!vertical?.deriveSf || !project) return null;
    try {
      return vertical.deriveSf(project.answers);
    } catch {
      // A bad answer combination should send them to the tranche picker, not
      // to a blank screen.
      return null;
    }
  }, [vertical, project]);

  if (loading) return <WizardStatus message="Working out your square footage…" />;
  if (loadError) return <WizardStatus message={loadError} />;
  if (!project || !vertical) {
    return <WizardStatus message="We could not find that project." />;
  }

  const showDerived = derived !== null && !overriding;
  const chosen = project.sizeTranche;
  const accepted = project.sizeSf !== null;

  const acceptDerived = () => {
    if (!derived) return;
    setField('sizeSf', Math.round((derived.low + derived.high) / 2));
    setField('sizeTranche', null);
  };

  const pickTranche = (t: SizeTranche) => {
    setField('sizeTranche', t.id);
    setField('sizeSf', Math.round((t.minSf + t.maxSf) / 2));
  };

  const blocked =
    showDerived && !accepted
      ? 'Accept the range below, or choose a size band instead.'
      : !showDerived && !chosen
        ? 'Pick the size band that sounds closest.'
        : undefined;

  const onContinue = async () => {
    setBusy(true);
    await flush();
    setBusy(false);
    navigate(`/project/${project.id}/system`);
  };

  return (
    <WizardShell
      step={2}
      verticalLabel={vertical.label}
      title="How big is this building?"
      intro="This is a starting range, not a number to build to. It moves once a design professional works through your program."
      saveState={saveState}
      onBack={() => navigate(`/project/${project.id}/questions`)}
      onContinue={() => void onContinue()}
      continueLabel="Choose how it is built"
      blockedReason={blocked}
      busy={busy}
    >
      {showDerived && derived ? (
        <div className="flex flex-col gap-6">
          <div
            className={cx(
              'rounded-lg border p-6 sm:p-8',
              accepted
                ? 'border-green-600 bg-green-050 shadow-[inset_0_0_0_2px_var(--color-green-600)]'
                : 'border-grey-200 bg-white shadow-sm',
            )}
          >
            <p className="t-eyebrow text-green-600">From your answers</p>
            <p className="t-numeral mt-3 text-3xl text-ink sm:text-4xl">
              {fmt(derived.low)} – {fmt(derived.high)} sf
            </p>
            <p className="t-body measure mt-4 text-grey-600">{derived.basis}</p>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={acceptDerived}
                aria-pressed={accepted}
                className={cx(
                  'min-h-tap rounded-full border px-6 text-[0.95rem] font-semibold transition-colors',
                  accepted
                    ? 'border-green-600 bg-green-600 text-white'
                    : 'border-green-600 bg-white text-green-700 hover:bg-green-050',
                )}
              >
                {accepted ? 'Using this range' : 'That sounds right'}
              </button>
              <button
                type="button"
                onClick={() => setOverriding(true)}
                className="min-h-tap rounded-full border border-grey-300 bg-white px-6 text-[0.95rem] text-ink transition-colors hover:bg-grey-050"
              >
                Pick a size myself
              </button>
            </div>
          </div>

          <Callout title="Where this comes from">
            We sized it from the answers you gave, using planning rules of thumb for this
            kind of building in the Southeast. Treat it as a range to test, and confirm it
            with your design professional.
          </Callout>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {derived && (
            <Callout title="You can go back to our estimate">
              From your answers we estimated {fmt(derived.low)} – {fmt(derived.high)} sf.{' '}
              <button
                type="button"
                onClick={() => setOverriding(false)}
                className="rounded-sm font-semibold text-green-700 underline decoration-1 underline-offset-4 hover:decoration-2"
              >
                Use that instead
              </button>
              .
            </Callout>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            {vertical.tranches.map((t) => (
              <TrancheOption
                key={t.id}
                tranche={t}
                selected={chosen === t.id}
                onSelect={() => pickTranche(t)}
              />
            ))}
          </div>
        </div>
      )}
    </WizardShell>
  );
}
